<template>
  <div class="media-options">
    <div
      v-if="currentPageThemeName === 'the-electric-company' && isVideoAsset"
      class="module-options__item module-options__item--theme"
    >
      <legend class="module-options__legend">
        {{ $t('Theme') }}
      </legend>
      <ButtonThemeSelect
        :canvas-name="canvasName"
        :module-data="moduleData"
        type="play-button"
        :variations="buttonThemeVariations"
      />
    </div>
    <div class="module-options__item module-options__item--layout">
      <legend class="module-options__legend">
        {{ $t('Layout') }}
      </legend>
      <NebulaSelect>
        <template #select>
          <select
            class="nebula-select__element"
            :value="moduleData.options.layout || 'top'"
            @change="onChangeLayout"
          >
            <NebulaSelectOption
              :option="{
                text: $t('Text Below'),
                value: 'top',
              }"
            />
            <NebulaSelectOption
              :option="{
                text: $t('Text Beside'),
                value: 'left',
              }"
            />
            <NebulaSelectOption
              :option="{
                text: $t('Image Only'),
                value: 'image',
              }"
            />
          </select>
        </template>
      </NebulaSelect>
    </div>

    <div
      v-if="!isAudioAsset && !useChannelCardLayout"
      class="module-options__item module-options__item--image-settings"
    >
      <legend class="module-options__legend">
        {{ $t('Image Settings') }}
      </legend>
      <NebulaSelect>
        <template #select>
          <select
            class="nebula-select__element"
            :value="moduleData.options.image_settings || 'letterbox'"
            @change="onChangeImageSettings"
          >
            <NebulaSelectOption
              :option="{
                text: $t('Fill'),
                value: 'fill',
              }"
            />
            <NebulaSelectOption
              :option="{
                text: $t('Size to Fit'),
                value: 'letterbox',
              }"
            />
          </select>
        </template>
      </NebulaSelect>
    </div>

    <div
      class="module-options__item module-options__item--display-settings"
      v-if="moduleData.options.layout !== 'image' && !useChannelCardLayout"
    >
      <legend class="module-options__legend">
        {{ $t('Display Options') }}
      </legend>
      <fieldset
        class="nebula-checkbox-group module-options__checkbox-group"
        role="group"
      >
        <NebulaCheckbox :label-text="$t('Title')">
          <template #checkbox>
            <input
              class="nebula-checkbox module-options__checkbox-input--title"
              type="checkbox"
              v-model="moduleData.options.show_title"
              @change="update"
            >
          </template>
        </NebulaCheckbox>
        <NebulaCheckbox :label-text="$t('Description')">
          <template #checkbox>
            <input
              class="nebula-checkbox"
              type="checkbox"
              v-model="moduleData.options.show_description"
              @change="update"
            >
          </template>
        </NebulaCheckbox>
        <NebulaCheckbox :label-text="$t('Type')">
          <template #checkbox>
            <input
              class="nebula-checkbox"
              type="checkbox"
              v-model="moduleData.options.show_type"
              @change="update"
            >
          </template>
        </NebulaCheckbox>
      </fieldset>
    </div>

    <div
      v-if="!isAudioAsset"
      class="module-options__item module-options__item--background"
    >
      <NebulaCheckbox :label-text="$t('Use Background Transparency')">
        <template #checkbox>
          <input
            class="nebula-checkbox"
            type="checkbox"
            v-model="moduleData.options.use_transparent_background"
            @change="update"
          >
        </template>
      </NebulaCheckbox>
    </div>

    <div
      v-if="isImageAsset"
      class="module-options__item module-options__item--zoom-and-action"
      :class="moduleData.options.action && 'module-options__item--full-width'"
    >
      <div class="module-options__item module-options__item--allow-zoom">
        <label class="media-options__toggle-wrapper">
          <span class="module-options__legend">
            {{ $t('Allow Zoom?') }}
          </span>
          <input
            type="checkbox"
            class="nebula-screenreader-only media-options__toggle-input"
            v-model="moduleData.options.allow_zoom"
            @change="update"
          >
          <div
            aria-hidden="true"
            class="media-options__toggle"
            :class="moduleData.options.allow_zoom && 'media-options__toggle--on'"
          >
            <div class="media-options__toggle-dot" />
          </div>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import {
  NebulaCheckbox,
  NebulaSelect,
  NebulaSelectOption,
} from '@discoveryedu/nebula-components';
import { cloneDeep, get } from 'lodash-es';
import { mapState } from 'pinia';
import ButtonThemeSelect from '@/components/ButtonThemeSelect.vue';
import { themes as themeConfigs } from '@/lib/constants/themes.json';
import { bus } from '@/lib/eventBus';
import {
  useEditorStore,
} from '@/stores';

export default {
  name: 'MediaOptions',
  components: {
    ButtonThemeSelect,
    NebulaCheckbox,
    NebulaSelect,
    NebulaSelectOption,
  },
  props: {
    canvasName: {
      type: String,
      required: true,
    },
    isAudioAsset: {
      type: Boolean,
      default: false,
    },
    isImageAsset: {
      type: Boolean,
      default: false,
    },
    isVideoAsset: {
      type: Boolean,
      default: false,
    },
    moduleData: {
      type: Object,
      required: true,
    },
    useChannelCardLayout: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update-module'],
  computed: {
    ...mapState(useEditorStore, [
      'currentPage',
    ]),
    buttonThemeVariations() {
      const currentPageThemeConfig = themeConfigs
        .find((theme) => theme.id === this.currentPageThemeName);

      return (currentPageThemeConfig.playButtonColors || [])
        .map((playButtonColor) => playButtonColor.id);
    },
    currentPageThemeName() {
      return get(this, 'currentPage.options.theme.name', 'default');
    },
  },
  methods: {
    onChangeImageSettings({ target }) {
      const data = cloneDeep(this.moduleData);
      data.options.image_settings = target.value;
      data.options.image_offset = undefined;
      this.$emit('update-module', data);
    },
    onChangeLayout({ target }) {
      const data = cloneDeep(this.moduleData);
      data.options.layout = target.value;
      data.options.image_offset = undefined;
      bus.emit('update-module-options'); // trigger the module wrapper to resize
      this.$emit('update-module', data);
    },
    update() {
      const data = cloneDeep(this.moduleData);
      this.$emit('update-module', data);
    },
  },
};
</script>

<style lang="stylus">
.media-options {
  display: grid;
  .module-options__item--theme {
    grid-area: 1 / 1 / 2 / 3;
    margin-bottom: $nebula-space-2x;
  }

  .module-options__item--layout {
    grid-area: 2 / 1 / 3 / 2;
    margin-bottom: $nebula-space-2x;
  }

  .module-options__item--image-settings {
    grid-area: 2 / 2 / 3 / 3;
    margin-bottom: $nebula-space-2x;
  }

  .module-options__item--display-settings {
    grid-area: 1 / 3 / 5 / 4;
  }

  .module-options__item--background {
    grid-area: 3 / 1 / 4 / 3;
    min-width: max-content;

    &:not(:last-child) {
      margin-bottom: $nebula-space-2x;
    }
  }

  .module-options__item--zoom-and-action {
    flex-direction: row;
    grid-area: 4 / 1 / 5 / 4;
  }

  .module-options__item--full-width {
    min-width: 500px;
  }

  &__toggle-dot {
    background-color: $nebula-color-white;
    border-radius: 50%;
    height: $nebula-space-3x;
    margin-inline-start: 0;
    transition: margin $nebula-transition-default;
    width: $nebula-space-3x;
  }

  &__toggle {
    align-items: center;
    background-color: $nebula-color-interface-blue-300;
    border-radius: $nebula-space-2x;
    display: flex;
    padding: $nebula-space-half;
    width: $nebula-space-8x;

    &--on {
      background-color: $nebula-color-interactive-blue-300;

      .media-options__toggle-dot {
        margin-inline-start: $nebula-space-4x;
      }
    }

    input:focus + & {
      outline: #7b869d dotted 1px;
      outline: -webkit-focus-ring-color auto 5px;
    }
  }
}
</style>
